import {useEffect, useRef} from "react";
import SportsEsportsOutlined from "@mui/icons-material/SportsEsportsOutlined";
import {InlineAction} from "components/InlineAction.styles";
import {useMessages} from "i18n";
import type {GameId} from "./gameCatalog";
import {playLabel} from "./gamePlayLabel";
import {OfferRow, OfferText, PlayButton} from "./GameSlot.styles";

interface PlayProps {
	game: GameId;
	onPlay: () => void;
	/** Take focus on mount: the game this control replaces was just closed with the ×. */
	focusOnMount?: boolean;
}

/**
 * The Play control, naming the game this transfer picked. `compact` is the success cards' share-row action
 * (kitecorp/transferify-ui#3320): ink label, a gamepad in the accent, opening the game's dialog. `shortLabel`
 * is a shorter visible word for a narrow row (the recipient card's strip, ui#2990): the game's full label stays
 * the accessible name, and contains the word.
 */
export function PlayGameButton({game, onPlay, focusOnMount = false, compact = false, shortLabel}: PlayProps & {
	compact?: boolean; shortLabel?: string;
}) {
	const {games} = useMessages();
	const control = useRef<HTMLButtonElement>(null);
	useEffect(() => {
		if (focusOnMount) control.current?.focus({preventScroll: true});
	}, [focusOnMount]);
	const label = playLabel(games, game);
	if (compact) {
		return (
			<InlineAction ref={control} type="button" onClick={onPlay} aria-haspopup="dialog">
				<SportsEsportsOutlined aria-hidden="true"/>{label}
			</InlineAction>
		);
	}
	return (
		<PlayButton ref={control} type="button" onClick={onPlay} aria-label={shortLabel === undefined ? undefined : label}>
			<svg viewBox="0 0 12 12" aria-hidden="true"><path d="M2 1.2v9.6L10.4 6z" fill="currentColor"/></svg>
			{shortLabel ?? label}
		</PlayButton>
	);
}

/**
 * "Play while you wait?" and the Play button, which names the game this transfer picked
 * (kitecorp/transferify-ui#2872, #2874). Shown only where `./gameSession`'s `offersPlay` says so; the
 * game never opens by itself (owner decision 2026-09-24).
 */
export function GameOffer(props: PlayProps) {
	const {games} = useMessages();
	return (
		<OfferRow>
			<OfferText>{games.common.offer}</OfferText>
			<PlayGameButton {...props}/>
		</OfferRow>
	);
}
